/**
 * useIndustryServices Hook
 * Generic CRUD operations for industry_services table
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryService, CreateServiceInput, UpdateServiceInput } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

export function useIndustryServices(domain: CompanyType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data } = await supabase.from('companies').select('*').eq('user_id', user.id).single();
      return data;
    },
  });

  const { data: services, isLoading, error, refetch } = useQuery({
    queryKey: ['industry_services', company?.id, domain],
    queryFn: async () => {
      if (!company) return [];
      const { data } = await supabase
        .from('industry_services')
        .select('*')
        .eq('company_id', company.id)
        .eq('domain', domain)
        .order('name');
      return (data || []) as IndustryService[];
    },
    enabled: !!company,
  });

  const createService = useMutation({
    mutationFn: async (input: CreateServiceInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from('industry_services')
        .insert({ ...input, domain, company_id: company!.id, user_id: user!.id })
        .select()
        .single();
      return data as IndustryService;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry_services', company?.id, domain] });
      toast({ title: 'Serviço criado com sucesso' });
    },
  });

  const updateService = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateServiceInput) => {
      const { data } = await supabase
        .from('industry_services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      return data as IndustryService;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry_services', company?.id, domain] });
      toast({ title: 'Serviço atualizado' });
    },
  });

  const deleteService = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('industry_services').delete().eq('id', id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry_services', company?.id, domain] });
      toast({ title: 'Serviço excluído' });
    },
  });

  return {
    services: services || [],
    isLoading,
    error,
    refetch,
    createService: createService.mutate,
    updateService: updateService.mutate,
    deleteService: deleteService.mutate,
  };
}
